import type { DieTypeQuantities } from "@/types";

const HTTPS = "https://";
const DEV_DDB_URL = "dev.dndbeyond.com";
const DEV_URL = `${HTTPS}${DEV_DDB_URL}`;
const getSuffix = (env: string) => (env === "production" ? "" : "-stg");
const isWorker =
  typeof self !== "undefined" &&
  typeof WorkerGlobalScope !== "undefined" &&
  self instanceof WorkerGlobalScope;
const workerNameData = isWorker
  ? JSON.parse(globalThis.self?.name || "{}")
  : {};
const searchParams = new URLSearchParams(globalThis.location?.search);

export const isDebug = Boolean(
  searchParams.get("isDebug") === "true" || workerNameData.isDebug,
);
export const isMobileApp = Boolean(
  searchParams.get("isMobileApp") === "true" || workerNameData.isMobileApp,
);
export const isVttView = Boolean(searchParams.get("view") === "vtt");

export const getIsProdEnv = (env: string) => Boolean(env === "production");
export const getDiceServiceUrl = (nodeEnv: string, env: string) =>
  nodeEnv === "development" && !isMobileApp
    ? `${DEV_URL}:2023`
    : `https://dice-service${getSuffix(env)}.dndbeyond.com`;
export const getMediaUrl = (nodeEnv: string, env: string) =>
  nodeEnv === "development" && !isMobileApp
    ? `${DEV_URL}:2030`
    : `https://media${getSuffix(env)}.dndbeyond.com`;
export const getAuthServiceUrl = (nodeEnv: string, env: string) =>
  nodeEnv === "development" && !isMobileApp
    ? `${DEV_URL}:2024/v1/cobalt-token`
    : `https://auth-service${getSuffix(env)}.dndbeyond.com/v1/cobalt-token`;

// combination of the OG size config value (which was 0.1 for each die type)
// and our own scaling adjustment to get dice to the relative size we want
export const ADDITIONAL_SCALING_FACTOR = 0.85;
export const SHADOW_OPACITY = 0.25;
export const PddPayloadTypes = {
  ComponentMounted: "componentMounted",
  CustomUrlMap: "customUrlMap",
  DiceSets: "diceSets",
  Manifests: "manifests",
  PlaySound: "playSound",
  UserSettings: "userSettings",
  ToastMessage: "toastMessage",
  ResetStore: "resetStore",
  RemoveEventListener: "removeEventListener",
  WorkerStats: "workerStats",
  Scales: "scales",
  RenderDice: "renderDice",
  RemoveRoll: "removeRoll",
  PreRoll: "preRoll",
  StartRoll: "startRoll",
} as const;
export const DiceActionTypes = {
  SetUpdate: "dice/set/update",
} as const;
export const DiceRollActionTypes = {
  Pending: "dice/roll/pending",
  Rejected: "dice/roll/rejected",
  Fulfilled: "dice/roll/fulfilled",
  Deferred: "dice/roll/deferred",
} as const;
export const BASE_ZOOM = 50;
const BASE_ZOOM_REFERENCE_HEIGHT = 900;
const MIN_BASE_ZOOM_MULTIPLIER = 0.7;
export const getBaseZoom = (clientHeight: number) => {
  const normalizedScale = Math.min(
    clientHeight / BASE_ZOOM_REFERENCE_HEIGHT,
    1,
  );
  const boundedScale = Math.max(normalizedScale, MIN_BASE_ZOOM_MULTIPLIER);

  return BASE_ZOOM * boundedScale;
};
export const CAMERA_Z = 2_000;
export const INTERACTION_PLANE_Z = -2;
// Sets how far in front of the camera objects will be visible.
// Ensures all map elements, including the lowest Z (like INTERACTION_PLANE_Z), are rendered.
// The `far` value is slightly beyond the camera to prevent clipping.
export const CAMERA_FRUSTUM = {
  near: 0.1,
  far: CAMERA_Z + Math.abs(INTERACTION_PLANE_Z) + 1,
};
export const RollTos = {
  Self: "Self",
  Everyone: "Everyone",
} as const;
// key on userData for animation targetId
export const ANIMATION_TARGET_ID = "animationTargetId" as const;
export const initialDieTypeQuantities: DieTypeQuantities = {
  d20: 0,
  d12: 0,
  d100: 0,
  d10: 0,
  d8: 0,
  d6: 0,
  d4: 0,
};

export const DieEventTypes = {
  ROLL_BEGIN: "rollBegin",
  COLLISION: "collision",
  ROLL_COMPLETE: "rollComplete", // _technically_ when a die settles
  MIN_ROLL: "minRoll",
  MAX_ROLL: "maxRoll",
  DIE_REMOVED: "dieRemoved", // _technically_ when a die should run its dispose animation
} as const;

export const initialEntityState = {
  entities: {},
  ids: [],
};

export const RollWiths = {
  adv: "Advantage",
  flat: "Flat (One Die)",
  disadv: "Disadvantage",
} as const;

export const RollTypes = {
  Roll: "roll",
  ToHit: "to hit",
  Damage: "damage",
  Heal: "heal",
  Spell: "spell",
  Save: "save",
  Check: "check",
} as const;

export const RollKinds = {
  Advantage: "advantage",
  Disadvantage: "disadvantage",
  CriticalHit: "critical hit",
} as const;

export const DieTypes = {
  d4: "d4",
  d6: "d6",
  d8: "d8",
  d10: "d10",
  d12: "d12",
  d20: "d20",
  d100: "d100",
} as const;

export const DiceVisibilities = {
  Shared: "shared",
  Self: "self",
  Disabled: "disabled",
} as const;

export const DISPOSE_LENGTH = 3.0;
export const SETTLED_LENGTH = 7.5;
